/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect } from 'react';
import IEnergyManagementProps, {
  ECarbonCartAt
} from '../IEnergyManagementProps';
import { Grid } from '@scuf/common';
import './EnergyManagementSiteDashboardContent.scss';
import './EmaasSiteDashboardCommonTable.scss';
import UtilityBillComponent from '../UtilityBillWidget/UtilityBillWidgetSite';
import CarbonEmissionKPISite from '../CarbonEmissionKPI/CarbonEmissionKPISite';
import EmaasEnergyUsageWidgetSite from '../EmaasEnergyUsage/EmaasEnergyUsageWidgetSite';
import PortfolioSummaryWidget from '../PortfolioSummary/PortfolioSummaryWidget';
import { ApiContext } from '../../../context/ApiContext';
import EnergyOptimizationSiteWidget from '../EnergyOptimization/EnergyOptimizationSiteWidget/EnergyOptimizationSiteWidget';
import { shouldComponentNotUpdateOnDateChange } from '../../shared/Utils/Utils';
import { UtilityCostType } from '../../shared/Utils/Constants';
import EnergyStarWidgetContent from '../../EnergyStar/EnergyStarWidgetContent';
import { EnergyUsageDataSourceType } from '../../shared/Enums';

const EnergyManagementSiteDashboardContent = React.memo(
  (props: IEnergyManagementProps) => {
    const {
      connectorConfig,
      bearerToken,
      requestContext,
      emaasContext,
      displayWidgets
    } = props;

    const apiContext = useContext(ApiContext);
    const toggleDisplay = (type: string) => {
      if (!displayWidgets || displayWidgets?.length === 0) return false;
      return displayWidgets && displayWidgets.includes(type);
    };

    const { dataSourceType, setDataSourceType } = apiContext;

    const {
      isEOSubscribed,
      onEnergyStarViewDetailsClick,
      showEnergyStarWidget
    } = apiContext;

    let cssCard = '';
    if (isEOSubscribed || showEnergyStarWidget) {
      cssCard = 'utility-eo-bill-kpi-card';
    }

    const [dataSourceTypeEnergy, setDataSourceTypeEnergy] = React.useState<
      EnergyUsageDataSourceType | string | null
    >(dataSourceType ? dataSourceType : null);

    const [dataSourceTypeCarbon, setDataSourceTypeCarbon] = React.useState<
      EnergyUsageDataSourceType | string | null
    >(
      emaasContext?.carbonDataSourceInitial
        ? emaasContext.carbonDataSourceInitial
          ? emaasContext.carbonDataSourceInitial
          : null
        : null
    );
    React.useEffect(() => {
      emaasContext?.setCarbonDataSourceInitial &&
        emaasContext?.setCarbonDataSourceInitial(dataSourceTypeCarbon);
    }, [dataSourceTypeCarbon]);

    React.useEffect(() => {
      setDataSourceType && setDataSourceType(dataSourceTypeEnergy);
    }, [dataSourceTypeEnergy]);

    const carbonWidget = (
      <CarbonEmissionKPISite
        connectorConfig={connectorConfig}
        bearerToken={bearerToken}
        requestContext={requestContext}
        carbonApiFor={ECarbonCartAt.siteLabel}
        dataSourceType={dataSourceTypeCarbon}
        setDataSourceType={setDataSourceTypeCarbon}
      />
    );
    const energyUsageWidget = (
      <EmaasEnergyUsageWidgetSite
        connectorConfig={connectorConfig}
        requestContext={requestContext}
        bearerToken={bearerToken}
        emaasContext={emaasContext}
        dataSourceType={dataSourceTypeEnergy}
        setDataSourceType={setDataSourceTypeEnergy}
      />
    );
    const utilityWidget = (
      <UtilityBillComponent
        connectorConfig={connectorConfig}
        bearerToken={bearerToken}
        requestContext={requestContext}
        widgetType={UtilityCostType.site}
      />
    );

    return toggleDisplay('carbon') ? (
      carbonWidget
    ) : toggleDisplay('energy') ? (
      energyUsageWidget
    ) : toggleDisplay('utility') ? (
      utilityWidget
    ) : (
      <Grid className={`layout-grid ${cssCard}`}>
        <Grid.Row className="layout-portfolio-summary-row">
          <Grid.Column className="emaas-layout-header" width={12}>
            <PortfolioSummaryWidget
              connectorConfig={connectorConfig}
              requestContext={requestContext}
              bearerToken={bearerToken}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="layout-content-grid-row">
          <Grid.Column
            width={4}
            xsWidth={12}
            className="energyusage-kpi-card emaas-siteview-dashboard"
          >
            {carbonWidget}
          </Grid.Column>
          <Grid.Column
            width={4}
            xsWidth={12}
            className="energyusage-kpi-card emaas-siteview-dashboard"
          >
            {energyUsageWidget}
          </Grid.Column>
          <Grid.Column
            width={4}
            xsWidth={12}
            className="energyusage-kpi-card emaas-siteview-dashboard energy-utility-kpi-card"
          >
            {isEOSubscribed && (
              <EnergyOptimizationSiteWidget
                bearerToken={bearerToken}
                requestContext={requestContext}
                connectorConfig={connectorConfig}
                mainGraph={true}
                isAllUtility={true}
              />
            )}
            {showEnergyStarWidget && (
              <EnergyStarWidgetContent
                connectorConfig={connectorConfig}
                requestContext={requestContext}
                bearerToken={bearerToken}
                emaasContext={emaasContext}
                onEnergyStarViewDetailsClick={onEnergyStarViewDetailsClick}
              />
            )}
            {utilityWidget}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  },
  shouldComponentNotUpdateOnDateChange
);

export default EnergyManagementSiteDashboardContent;
